import React, { useEffect, useState, useCallback, useRef } from "react";
import { skipGreeting } from "../textPreview";
import { Link } from "react-router-dom";
import { useSpecialty } from "../SpecialtyContext";
import { API_URL } from "../config";
import PopularWidget from "./PopularWidget";
import TopAuthorsWidget from "./TopAuthorsWidget";
import SpecialtyEditorBanner from "./SpecialtyEditorBanner";
import ReadWatchToggle from "./ReadWatchToggle";
import { AuthorLink } from "../authorLookup";

const TAG_COLORS = {
  "Научная статья": "#007c84",
  "Обзор": "#006870",
  "Клинический случай": "#15803d",
  "Рекомендации": "#e5005b",
};

const PAGE_SIZE = 10;

export default function Articles({ onLoginRequest }) {
  const [articles, setArticles] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const { specialty } = useSpecialty();
  const searchDebounce = useRef(null);

  // Debounce поискового поля
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const fetchPage = useCallback((offset) => {
    const params = new URLSearchParams();
    if (specialty?.id) params.set("specialty", specialty.id);
    if (search) params.set("search", search);
    params.set("limit", PAGE_SIZE);
    params.set("offset", offset);
    return fetch(`${API_URL}/posts?${params}`).then(r => r.ok ? r.json() : []);
  }, [specialty, search]);

  // Перезагрузка при смене специальности/поиска
  useEffect(() => {
    setActiveTag(null);
    setLoading(true);
    fetchPage(0)
      .then(data => {
        setArticles(data);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch(() => { setArticles([]); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [fetchPage]);

  const loadMore = () => {
    setLoading(true);
    fetchPage(articles.length)
      .then(data => {
        setArticles(prev => [...prev, ...data]);
        setHasMore(data.length === PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  };

  const tagColor = tag => TAG_COLORS[tag] || "#2e7d32";

  const allTags = [...new Set(
    articles.flatMap(a => a.tags ? a.tags.split(";").map(t => t.trim()).filter(Boolean) : [])
  )];

  const visibleArticles = activeTag
    ? articles.filter(a => (a.tags || "").split(";").map(t => t.trim()).includes(activeTag))
    : articles;

  // Первые ~300 символов для превью
  const preview = text => {
    if (!text) return "";
    const plain = text
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")   // убираем картинки целиком
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // ссылки → только текст
      .replace(/[#*_`>\-[\]]/g, "")
      .trim();
    const shown = skipGreeting(plain.replace(/\s+/g, " "));
    return shown.length > 280 ? shown.slice(0, 280) + "…" : shown;
  };

  return (
    <section>
      <h1 className="section-title">Статьи и научные разработки</h1>
      <ReadWatchToggle />

      <SpecialtyEditorBanner
        specialty={specialty?.id}
        specialtyLabel={specialty?.label}
        onLoginRequest={onLoginRequest}
      />

      <PopularWidget />
      <TopAuthorsWidget />

      <div style={{ marginBottom: 16 }}>
        <input
          type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
          placeholder="Поиск по статьям…"
          style={{ width: "100%", maxWidth: 360, padding: "8px 14px", borderRadius: 8,
            border: "1px solid #c4ceda", fontSize: "0.92rem", outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {allTags.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {allTags.map(t => (
            <button
              key={t}
              onClick={() => setActiveTag(prev => prev === t ? null : t)}
              style={{
                background: activeTag === t ? tagColor(t) : "#f0f2f8",
                color: activeTag === t ? "#fff" : "#444",
                border: "none", borderRadius: 14, padding: "4px 12px",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer"
              }}
            >
              {t}
            </button>
          ))}
          {activeTag && (
            <button onClick={() => setActiveTag(null)}
              style={{ background: "none", color: "#888", border: "none",
                fontSize: "0.82rem", cursor: "pointer", textDecoration: "underline" }}>
              сбросить
            </button>
          )}
        </div>
      )}

      {!loading && visibleArticles.length === 0 && (
        <p style={{ color: "#999", fontStyle: "italic" }}>
          {search ? "Ничего не найдено." : activeTag ? "Нет статей с этим тегом." : "Статей пока нет. Добавьте первую через CMS."}
        </p>
      )}
      {visibleArticles.map(a => {
        const tags = a.tags ? a.tags.split(";").map(t => t.trim()).filter(Boolean) : [];
        return (
          <div className="card" key={a.id}>
            {/* Шапка карточки */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              {tags.map(t => (
                <span key={t} onClick={() => setActiveTag(t)}
                  style={{ background: tagColor(t), color: "#fff", borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
                  {t}
                </span>
              ))}
              <span style={{ fontSize: "0.85rem", color: "#aaa", marginLeft: "auto" }}>
                {a.created_at ? new Date(a.created_at).toLocaleDateString("ru") : ""}
              </span>
            </div>

            {/* Заголовок */}
            <Link to={`/articles/${a.id}`} style={{ textDecoration: "none" }}>
              <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "#007c84", marginBottom: 6 }}>
                {a.title}
              </div>
            </Link>

            {/* Превью */}
            <div style={{ color: "#555", fontSize: "0.97rem", marginBottom: 8, lineHeight: 1.55 }}>
              {preview(a.content)}
            </div>

            {/* Футер */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
              <span style={{ fontSize: "0.88rem", color: "#888" }}>
                {a.author ? <>Автор: <AuthorLink name={a.author} style={{ color: "#007c84" }} /></> : ""}
              </span>
              <Link
                to={`/articles/${a.id}`}
                style={{ background: "none", color: "#e5005b", border: "1.5px solid #e5005b", borderRadius: 7, padding: "4px 14px", cursor: "pointer", fontSize: "0.9rem", textDecoration: "none" }}>
                Читать полностью →
              </Link>
            </div>
          </div>
        );
      })}

      {loading && <p style={{ color: "#888", textAlign: "center" }}>Загрузка…</p>}

      {!loading && hasMore && !activeTag && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button onClick={loadMore}
            style={{ background: "#f0f2f8", color: "#007c84", border: "1.5px solid #c4ceda",
              borderRadius: 8, padding: "8px 24px", cursor: "pointer", fontSize: "0.92rem" }}>
            Показать ещё
          </button>
        </div>
      )}
    </section>
  );
}
